import React, { ChangeEvent, FC, useCallback, useId, useMemo } from 'react';

import {
	EvFlexColumn,
	EvFlexRow,
	EvHeadingSmall,
	EvInfoTooltip,
	EvLabelDefault,
	EvTextArea,
	EvTextInput,
	TEXT_COLOR
} from '@evinced-private/ui-common';

import {
	LABEL_MANAGEMENT_PANEL_DESCRIPTION_INPUT_DAID,
	LABEL_MANAGEMENT_PANEL_DISPLAY_NAME_INPUT_DAID,
	LABEL_MANAGEMENT_PANEL_KEY_DISPLAY_DAID,
	LABEL_MANAGEMENT_PANEL_KEY_INPUT_DAID
} from 'data-automation-ids/LabelManagementAutomationIds';

import { DESCRIPTION_TEXTAREA_ROWS } from './LabelDetailsSection.constants';
import { LABEL_DETAILS_LABELS } from './LabelDetailsSection.labels';
import { CustomLabelDetailsSectionProps } from './LabelDetailsSection.types';

export const CustomLabelDetailsSection: FC<CustomLabelDetailsSectionProps> = ({
	displayName,
	description,
	labelKey,
	isKeyEditable,
	keyError,
	onDisplayNameChange,
	onDescriptionChange,
	onKeyChange,
	displayNameError,
	descriptionError
}) => {
	const displayNameId = useId();
	const keyInputId = useId();

	const keyPlaceholder = useMemo(() => {
		if (isKeyEditable) {
			return LABEL_DETAILS_LABELS.KEY_PLACEHOLDER;
		}
		return undefined;
	}, [isKeyEditable]);

	const handleDescriptionChange = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>) => {
			onDescriptionChange(event.target.value);
		},
		[onDescriptionChange]
	);

	return (
		<EvFlexColumn gap="LARGE">
			<EvHeadingSmall as="h3" color={TEXT_COLOR.CONTENT_2}>
				{LABEL_DETAILS_LABELS.SECTION_TITLE}
			</EvHeadingSmall>
			<EvFlexRow gap="LARGE" align="START">
				<EvFlexColumn flex="1" gap="SMALL">
					<EvLabelDefault as="label" htmlFor={keyInputId}>
						{LABEL_DETAILS_LABELS.KEY_LABEL}
					</EvLabelDefault>
					<EvTextInput
						id={keyInputId}
						value={labelKey ?? ''}
						onChange={isKeyEditable ? onKeyChange : undefined}
						isRequired={isKeyEditable}
						isDisabled={!isKeyEditable}
						placeholder={keyPlaceholder}
						error={isKeyEditable ? keyError : undefined}
						dataAutomationId={
							isKeyEditable
								? LABEL_MANAGEMENT_PANEL_KEY_INPUT_DAID
								: LABEL_MANAGEMENT_PANEL_KEY_DISPLAY_DAID
						}
					/>
				</EvFlexColumn>
				<EvFlexColumn flex="1" gap="SMALL">
					<EvFlexRow align="CENTER" gap="SMALL">
						<EvLabelDefault as="label" htmlFor={displayNameId}>
							{LABEL_DETAILS_LABELS.DISPLAY_NAME_LABEL}
						</EvLabelDefault>
						<EvInfoTooltip title={LABEL_DETAILS_LABELS.DISPLAY_NAME_LABEL}>
							{LABEL_DETAILS_LABELS.DISPLAY_NAME_TOOLTIP}
						</EvInfoTooltip>
					</EvFlexRow>
					<EvTextInput
						id={displayNameId}
						value={displayName}
						onChange={onDisplayNameChange}
						isRequired
						placeholder={LABEL_DETAILS_LABELS.DISPLAY_NAME_PLACEHOLDER}
						error={displayNameError}
						dataAutomationId={LABEL_MANAGEMENT_PANEL_DISPLAY_NAME_INPUT_DAID}
					/>
				</EvFlexColumn>
			</EvFlexRow>
			<EvTextArea
				label={LABEL_DETAILS_LABELS.DESCRIPTION_LABEL}
				tooltip={LABEL_DETAILS_LABELS.DESCRIPTION_TOOLTIP}
				value={description}
				onChange={handleDescriptionChange}
				dataAutomationId={LABEL_MANAGEMENT_PANEL_DESCRIPTION_INPUT_DAID}
				error={descriptionError}
				rows={DESCRIPTION_TEXTAREA_ROWS}
				hint={LABEL_DETAILS_LABELS.DESCRIPTION_HINT}
			/>
		</EvFlexColumn>
	);
};
